import { useQuery } from "@tanstack/react-query";
import { Typography } from "antd";
import {
  CompanyList,
  JournalRecordPreviewInfoRow,
  JournalRecordPreviewPanel,
  UserBubbleLinkText,
  statusIcons,
} from "@shared/ui";
import {
  fallbackString,
  formatDate,
  formatDatetime,
  formatMoscowDatetime,
  getLatestIsoDate,
} from "@shared/utils";
import { s3AdapterApi } from "../../../api";
import { journalsQueries } from "../../api/journals.queries";
import { $journalApi } from "../../fetch/fetch-client";
import { getStatusWithIcon } from "../../helpers/status-map";
import { DocumentDownloadButton } from "../document-download-button";
import "./journal-record-preview.scss";

export type JournalRecordPreviewSection = 1 | 2 | 3 | 4;

export type JournalRecordPreviewMeta = {
  itemNoVersionId: string;
  sequenceNumber?: number;
  status?: string;
  isCurrent?: boolean;
};

type JournalRecordPreviewProps = {
  sectionNumber: JournalRecordPreviewSection;
  record: JournalRecordPreviewMeta | null;
  onClose: () => void;
  onOpenRecord: (itemNoVersionId: string) => void;
};

type PreviewUser = {
  userId?: string;
  firstName?: string;
  surname?: string;
  middleName?: string;
  position?: string;
  userSurname?: string;
  userFirstName?: string;
  userMiddleName?: string;
};

const WORK_TYPE_LABELS: Record<string, string> = {
  WORK: "Работа",
  TEST: "Испытание",
};

const getShortFio = (user?: PreviewUser | null) => {
  const surname = user?.surname ?? user?.userSurname ?? "";
  const firstName = user?.firstName ?? user?.userFirstName ?? "";
  const middleName = user?.middleName ?? user?.userMiddleName ?? "";

  if (!surname && !firstName) return "-";

  const initials = `${firstName ? `${firstName[0]}.` : ""}${middleName ? `${middleName[0]}.` : ""}`;
  return `${surname} ${initials}`.trim();
};

const getSignerComment = (comments?: Array<{ type?: string; comment?: string }> | null) => {
  const comment = comments?.find((item) => item.type === "signer")?.comment;
  return comment?.trim() || null;
};

const useRecordPreviewQuery = (
  sectionNumber: JournalRecordPreviewSection,
  itemNoVersionId?: string,
) => {
  const enabled = Boolean(itemNoVersionId);

  const sectionOneQuery = useQuery({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-one/items/{itemNoVersionId}",
      { params: { path: { itemNoVersionId: itemNoVersionId || "" } } },
    ),
    enabled: enabled && sectionNumber === 1,
  });

  const sectionTwoQuery = useQuery({
    ...$journalApi.queryOptions(
      "get",
      "/api/v1/journals/section-two/items/{itemNoVersionId}",
      { params: { path: { itemNoVersionId: itemNoVersionId || "" } } },
    ),
    enabled: enabled && sectionNumber === 2,
  });

  const sectionThreeQuery = useQuery({
    ...journalsQueries.getSectionThreeItem({ itemNoVersionId: itemNoVersionId || "" }),
    enabled: enabled && sectionNumber === 3,
  });

  const sectionFourQuery = useQuery({
    ...journalsQueries.getSectionFourItem({ itemNoVersionId: itemNoVersionId || "" }),
    enabled: enabled && sectionNumber === 4,
  });

  switch (sectionNumber) {
    case 1:
      return sectionOneQuery;
    case 2:
      return sectionTwoQuery;
    case 3:
      return sectionThreeQuery;
    case 4:
      return sectionFourQuery;
    default:
      return sectionOneQuery;
  }
};

const RecordPreviewStatusBadge = ({
  status,
  isCurrent,
  hideDeletedBadge = false,
}: {
  status?: string;
  isCurrent?: boolean;
  hideDeletedBadge?: boolean;
}) => {
  if (isCurrent === false && !hideDeletedBadge) {
    return (
      <span className="journal-record-preview-panel__status-badge journal-record-preview-panel__status-badge--deleted">
        {getStatusWithIcon("REJECTED").icon}
        Удалено
      </span>
    );
  }

  const statusInfo = getStatusWithIcon(status || "NEW");
  return (
    <span className="journal-record-preview-panel__status-badge">
      {statusInfo.icon}
      {statusInfo.status}
    </span>
  );
};

const PreviewComment = ({ comment }: { comment: string | null }) =>
  comment ? (
    <Typography.Text className="journal-record-preview__block-value">
      Комментарий: {comment}
    </Typography.Text>
  ) : null;

const PreviewPersonBlock = ({
  label,
  user,
  status,
}: {
  label: string;
  user?: PreviewUser;
  status?: string;
}) => (
  <div className="journal-record-preview__block">
    <Typography.Text className="journal-record-preview__block-label">{label}</Typography.Text>
    <div className="journal-record-preview__person-row">
      {user?.userId ? (
        <UserBubbleLinkText userId={user.userId} trigger="hover">
          {getShortFio(user)}
        </UserBubbleLinkText>
      ) : (
        <Typography.Text>{fallbackString(getShortFio(user))}</Typography.Text>
      )}
      {status && status !== "NEW" ? (
        <span className="journal-record-preview__status-icon">
          {statusIcons[status as keyof typeof statusIcons]}
        </span>
      ) : null}
    </div>
    {user?.position ? (
      <Typography.Text className="journal-record-preview__position">{user.position}</Typography.Text>
    ) : null}
  </div>
);

const SectionOneContent = ({ item }: { item: any }) => (
  <div className="journal-record-preview">
    <div className="journal-record-preview__section">
      <JournalRecordPreviewInfoRow label="Дата начала работ">
        {fallbackString(formatDate(item?.dateStart))}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Дата окончания работ">
        {fallbackString(formatDate(item?.dateEnd))}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Вид работ">
        {fallbackString(item?.workType)}
      </JournalRecordPreviewInfoRow>
    </div>
    <div className="journal-record-preview__section">
      <JournalRecordPreviewInfoRow label="Дата создания записи">
        {fallbackString(formatMoscowDatetime(item?.createDate))}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Дата редактирования">
        {fallbackString(formatMoscowDatetime(item?.updateDate))}
      </JournalRecordPreviewInfoRow>
    </div>
    <div className="journal-record-preview__block">
      <Typography.Text className="journal-record-preview__block-label">
        Полное и (или) сокращенное наименование или фамилия, имя отчество (последнее - при наличии)
        лица, осуществляющего строительство
      </Typography.Text>
      <Typography.Text className="journal-record-preview__block-value">
        {fallbackString(item?.company?.fullName ?? item?.company?.shortName)}
      </Typography.Text>
    </div>
    <div className="journal-record-preview__block">
      <Typography.Text className="journal-record-preview__block-label">
        Фамилия, инициалы, должность (при наличии) лица, входящего в список инженерно-технического
        персонала
      </Typography.Text>
      {item?.userCompany?.user?.userId ? (
        <UserBubbleLinkText userId={item.userCompany.user.userId} trigger="hover">
          {getShortFio(item.userCompany.user)}
        </UserBubbleLinkText>
      ) : (
        <Typography.Text>{fallbackString(getShortFio(item?.userCompany?.user))}</Typography.Text>
      )}
      {item?.userCompany?.user?.position ? (
        <Typography.Text className="journal-record-preview__position">
          {item.userCompany.user.position}
        </Typography.Text>
      ) : null}
    </div>
    <PreviewPersonBlock
      label="Уполномоченный представитель лица, осуществляющего строительство"
      user={item?.signer}
      status={item?.status}
    />
    <PreviewComment comment={getSignerComment(item?.comments)} />
  </div>
);

const SectionTwoContent = ({
  item,
  fileId,
  fileName,
}: {
  item: any;
  fileId?: string;
  fileName?: string;
}) => (
  <div className="journal-record-preview">
    <div className="journal-record-preview__section">
      <JournalRecordPreviewInfoRow label="Наименование специального журнала">
        {fallbackString(item?.specialJournalName)}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Дата передачи журнала">
        {fallbackString(formatDate(item?.dateHandover))}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Цифровая копия журнала">
        {fileId ? (
          <DocumentDownloadButton fileId={fileId} fileName={fileName || "document.pdf"} />
        ) : (
          "-"
        )}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Дата создания записи">
        {fallbackString(formatMoscowDatetime(item?.createDate))}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Дата редактирования">
        {fallbackString(formatMoscowDatetime(item?.updateDate))}
      </JournalRecordPreviewInfoRow>
    </div>

    {(item?.journalKeepersList ?? []).map((keeper: any, index: number) => {
      const orgName = keeper?.company?.fullName ?? keeper?.company?.shortName ?? "-";

      return (
        <div key={keeper?.company?.id || index} className="journal-record-preview__group">
          <JournalRecordPreviewInfoRow
            label="Организация, осуществляющая строительство или организация, осуществляющая подготовку проектной документации, ведущая журнал"
            alignTop
          >
            {fallbackString(orgName)}
          </JournalRecordPreviewInfoRow>
          <Typography.Text className="journal-record-preview__block-label">
            Представители, уполномоченный на ведение журнала
          </Typography.Text>
          {(keeper?.authorizedRepresentativesList ?? []).map((rep: any, repIndex: number) => (
            <div key={rep.userId || repIndex} className="journal-record-preview__rep">
              <JournalRecordPreviewInfoRow label="ФИО" alignTop>
                {rep?.userId ? (
                  <UserBubbleLinkText userId={rep.userId} trigger="hover">
                    {getShortFio(rep)}
                  </UserBubbleLinkText>
                ) : (
                  fallbackString(getShortFio(rep))
                )}
              </JournalRecordPreviewInfoRow>
              <JournalRecordPreviewInfoRow label="Должность">
                {fallbackString(rep?.position)}
              </JournalRecordPreviewInfoRow>
            </div>
          ))}
          <Typography.Text className="journal-record-preview__block-label">
            Уполномоченный представитель застройщика или технического заказчика, лица, ответственного
            за эксплуатацию здания, сооружения, и (или) регионального оператора
          </Typography.Text>
          <div className="journal-record-preview__person-row">
            {item?.signer?.userId ? (
              <UserBubbleLinkText userId={item.signer.userId} trigger="hover">
                {getShortFio(item.signer)}
              </UserBubbleLinkText>
            ) : (
              fallbackString(getShortFio(item?.signer))
            )}
            {item?.status && item.status !== "NEW" ? (
              <span className="journal-record-preview__status-icon">
                {statusIcons[item.status as keyof typeof statusIcons]}
              </span>
            ) : null}
          </div>
        </div>
      );
    })}

    <PreviewComment comment={getSignerComment(item?.comments)} />
  </div>
);

const SectionThreeContent = ({ item }: { item: any }) => (
  <div className="journal-record-preview">
    <div className="journal-record-preview__section">
      <JournalRecordPreviewInfoRow label="Наименование работ">
        {fallbackString(item?.workName)}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Тип работы">
        {fallbackString(WORK_TYPE_LABELS[item?.workType] ?? item?.workType)}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Дата выполнения работ">
        {fallbackString(formatDate(item?.workDate))}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Местоположение">
        {fallbackString(item?.locationDescription)}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Связь с исполнительной документацией">
        {item?.actConnect ? "есть" : "нет"}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Организации выполнившие работу" alignTop>
        <CompanyList companies={item?.companyList} separator="comma" />
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Дата создания записи">
        {fallbackString(formatMoscowDatetime(item?.createDate))}
      </JournalRecordPreviewInfoRow>
      <JournalRecordPreviewInfoRow label="Дата редактирования">
        {fallbackString(formatMoscowDatetime(item?.updateDate))}
      </JournalRecordPreviewInfoRow>
    </div>
    <PreviewPersonBlock
      label="Уполномоченный представитель лица, осуществляющего строительство"
      user={item?.signer}
      status={item?.status}
    />
    <PreviewComment comment={getSignerComment(item?.comments)} />
  </div>
);

const SectionFourSignerBlock = ({
  label,
  part,
}: {
  label: string;
  part?: {
    status?: string;
    inspectionEmployee?: PreviewUser;
    defectFixingInspectionEmployee?: PreviewUser;
    comments?: Array<{ type?: string; comment?: string }>;
  };
}) => {
  const employee = part?.inspectionEmployee ?? part?.defectFixingInspectionEmployee;

  return (
    <div className="journal-record-preview__block">
      <Typography.Text className="journal-record-preview__block-label">{label}</Typography.Text>
      <div className="journal-record-preview__person-row">
        {employee?.userId ? (
          <UserBubbleLinkText userId={employee.userId} trigger="hover">
            {getShortFio(employee)}
          </UserBubbleLinkText>
        ) : (
          <Typography.Text>{fallbackString(getShortFio(employee))}</Typography.Text>
        )}
        {part?.status && part.status !== "NEW" ? (
          <span className="journal-record-preview__status-icon">
            {statusIcons[part.status as keyof typeof statusIcons]}
          </span>
        ) : null}
      </div>
      {employee?.position ? (
        <Typography.Text className="journal-record-preview__position">
          {fallbackString(employee.position)}
        </Typography.Text>
      ) : null}
      <PreviewComment comment={getSignerComment(part?.comments)} />
    </div>
  );
};

const SectionFourContent = ({ item }: { item: any }) => {
  const partOne = item?.itemPartOne;
  const partTwo = item?.itemPartTwo;
  const hasDefects = partOne?.isDefects;
  const beginDate = formatDatetime(partOne?.controlEventPeriod?.beginDate);
  const endDate = formatDatetime(partOne?.controlEventPeriod?.endDate);
  const controlPeriod = beginDate && endDate ? `от ${beginDate} до ${endDate}` : "-";

  return (
    <div className="journal-record-preview">
      <div className="journal-record-preview__section">
        <JournalRecordPreviewInfoRow label="Сроки контрольного мероприятия" alignTop>
          {controlPeriod}
        </JournalRecordPreviewInfoRow>
        <JournalRecordPreviewInfoRow label="Сведения о проведении строительного контроля" alignTop>
          {fallbackString(partOne?.constructionControlInfo)}
        </JournalRecordPreviewInfoRow>
        <JournalRecordPreviewInfoRow label="Дата создания записи">
          {fallbackString(formatMoscowDatetime(item?.createDate))}
        </JournalRecordPreviewInfoRow>
        <JournalRecordPreviewInfoRow label="Дата редактирования">
          {fallbackString(
            formatMoscowDatetime(
              getLatestIsoDate(
                item?.updateDate,
                partOne?.updateDate,
                partTwo?.updateDate,
              ),
            ),
          )}
        </JournalRecordPreviewInfoRow>
        <JournalRecordPreviewInfoRow label="Выявленные недостатки" alignTop>
          {fallbackString(partOne?.defectDescriptionText)}
        </JournalRecordPreviewInfoRow>
        <JournalRecordPreviewInfoRow label="Плановая дата устранения нарушений">
          {fallbackString(formatDate(partOne?.defectFixingPlanDate))}
        </JournalRecordPreviewInfoRow>
      </div>

      <SectionFourSignerBlock
        label="Уполномоченный представитель по вопросам строительного контроля"
        part={partOne}
      />

      <Typography.Text className="journal-record-preview__block-label">
        Фактическая дата устранения нарушений и ответственный
      </Typography.Text>
      {!hasDefects ? (
        <Typography.Text>Замечания не выявлены</Typography.Text>
      ) : (
        <>
          <JournalRecordPreviewInfoRow label="Фактическая дата устранения нарушений">
            {fallbackString(formatDatetime(partTwo?.defectFixingFactDate))}
          </JournalRecordPreviewInfoRow>
          <JournalRecordPreviewInfoRow label="Ответственный">
            {partTwo?.defectFixingResponsibleEmployee?.userId ? (
              <UserBubbleLinkText
                userId={partTwo.defectFixingResponsibleEmployee.userId}
                trigger="hover"
              >
                {getShortFio(partTwo.defectFixingResponsibleEmployee)}
              </UserBubbleLinkText>
            ) : (
              fallbackString(getShortFio(partTwo?.defectFixingResponsibleEmployee))
            )}
          </JournalRecordPreviewInfoRow>
          <SectionFourSignerBlock
            label="Уполномоченный представитель по вопросам строительного контроля"
            part={partTwo}
          />
        </>
      )}

      {!hasDefects ? (
        <div className="journal-record-preview__block">
          <Typography.Text className="journal-record-preview__block-label">
            Уполномоченный представитель по вопросам строительного контроля
          </Typography.Text>
          <Typography.Text className="journal-record-preview__block-value">-</Typography.Text>
        </div>
      ) : null}
    </div>
  );
};

const PreviewContent = ({
  sectionNumber,
  item,
  journalScanId,
  journalScanFileName,
}: {
  sectionNumber: JournalRecordPreviewSection;
  item: unknown;
  journalScanId?: string;
  journalScanFileName?: string;
}) => {
  switch (sectionNumber) {
    case 1:
      return <SectionOneContent item={item} />;
    case 2:
      return (
        <SectionTwoContent
          item={item}
          fileId={journalScanId}
          fileName={journalScanFileName}
        />
      );
    case 3:
      return <SectionThreeContent item={item} />;
    case 4:
      return <SectionFourContent item={item} />;
    default:
      return null;
  }
};

export const JournalRecordPreview = ({
  sectionNumber,
  record,
  onClose,
  onOpenRecord,
}: JournalRecordPreviewProps) => {
  const open = Boolean(record?.itemNoVersionId);
  const { data, isLoading, isFetching } = useRecordPreviewQuery(
    sectionNumber,
    record?.itemNoVersionId,
  );

  const item = data as any;
  const journalScanId = item?.journalScanId as string | undefined;

  const { data: fileMeta } = useQuery<{ fileId?: string; fileName?: string }[]>({
    queryKey: ["s3-file-meta", journalScanId],
    queryFn: async () => {
      const response = await s3AdapterApi.post("/files/meta", [
        { fileNumber: 0, fileId: journalScanId },
      ]);
      return response.data;
    },
    enabled: open && sectionNumber === 2 && Boolean(journalScanId),
  });

  return (
    <JournalRecordPreviewPanel
      open={open}
      onClose={onClose}
      sequenceNumber={record?.sequenceNumber}
      statusBadge={
        <RecordPreviewStatusBadge
          status={record?.status ?? item?.status}
          isCurrent={record?.isCurrent ?? item?.isCurrent}
          hideDeletedBadge={sectionNumber === 4}
        />
      }
      isLoading={isLoading || isFetching}
      onOpenRecord={
        record?.itemNoVersionId ? () => onOpenRecord(record.itemNoVersionId) : undefined
      }
    >
      {item ? (
        <PreviewContent
          sectionNumber={sectionNumber}
          item={item}
          journalScanId={journalScanId}
          journalScanFileName={fileMeta?.[0]?.fileName}
        />
      ) : null}
    </JournalRecordPreviewPanel>
  );
};

export { isTablePreviewClick } from "@shared/ui";
